<script setup>
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { useAppToast } from '@/shared/lib/useAppToast'
import {
  createAttribute,
  updateAttribute
} from '@/modules/admin/products/attributes/api/attributes.service'

const props = defineProps({
  attribute: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'saved'])

const toast = useAppToast()

const loading = ref(false)
const form = ref({
  id: null,
  name: ''
})

const fieldErrors = ref({
  name: ''
})

const dialogTitle = computed(() =>
  form.value.id ? 'Редактировать атрибут' : 'Создать атрибут'
)

watch(
  () => props.attribute,
  (newVal) => {
    if (newVal) {
      form.value = {
        id: newVal.id ?? null,
        name: newVal.name ?? ''
      }
    } else {
      resetForm()
    }

    clearErrors()
  },
  { immediate: true }
)

function resetForm() {
  form.value = {
    id: null,
    name: ''
  }
}

function clearErrors() {
  fieldErrors.value = {
    name: ''
  }
}

function validateForm() {
  clearErrors()

  if (!form.value.name.trim()) {
    fieldErrors.value.name = 'Введите название атрибута'
  }

  return !Object.values(fieldErrors.value).some(Boolean)
}

async function saveAttribute() {
  if (!validateForm()) return

  try {
    loading.value = true

    const payload = {
      id: form.value.id,
      name: form.value.name.trim()
    }

    if (form.value.id) {
      await updateAttribute(form.value.id, payload)
      toast.success('Атрибут обновлён')
    } else {
      await createAttribute(payload)
      toast.success('Атрибут создан')
    }

    emit('saved')
  } catch (error) {
    fieldErrors.value.name = error.fieldErrors?.name || error.fieldErrors?.Name || ''
    toast.error(error.message || 'Не удалось сохранить атрибут')
  } finally {
    loading.value = false
  }
}

function closeDialog() {
  if (loading.value) return
  emit('close')
}
</script>

<template>
  <Dialog
    :visible="true"
    modal
    :header="dialogTitle"
    :style="{ width: 'min(460px, 95vw)' }"
    @update:visible="closeDialog"
  >
    <div class="form-container">
      <div class="field">
        <label for="attribute-name">Название атрибута</label>
        <InputText
          id="attribute-name"
          v-model="form.name"
          placeholder="Введите название атрибута"
          class="w-full"
          :invalid="Boolean(fieldErrors.name)"
          @input="fieldErrors.name = ''"
        />
        <small v-if="fieldErrors.name" class="field-error">
          {{ fieldErrors.name }}
        </small>
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        :disabled="loading"
        @click="closeDialog"
      />
      <Button
        :label="form.id ? 'Сохранить' : 'Создать'"
        :icon="form.id ? 'pi pi-check' : 'pi pi-plus'"
        :loading="loading"
        :disabled="loading"
        @click="saveAttribute"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-error {
  font-size: 12px;
  color: #dc2626;
}

:deep(.p-inputtext) {
  border-radius: 12px;
}

:deep(.p-inputtext.p-invalid) {
  border-color: #dc2626;
}
</style>